import { useState, useEffect } from "react";
import API_BASE_URL from "../config";

const STATUS_STYLES = {
  Approved: "bg-green-100 text-green-700 border border-green-200",
  Pending:  "bg-yellow-100 text-yellow-700 border border-yellow-200",
  Rejected: "bg-red-100 text-red-600 border border-red-200",
  Draft:    "bg-gray-100 text-gray-500 border border-gray-200",
};

const STATUS_DOT = {
  Approved: "bg-green-500",
  Pending:  "bg-yellow-400",
  Rejected: "bg-red-500",
  Draft:    "bg-gray-400",
};

const STATUSES = ["All", "Approved", "Pending", "Rejected", "Draft"];

const getStatus = (row) => row.status || "Pending"; // fallback if backend has no status field yet

export default function FG_Withdrawal_FORM_LIST() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selected, setSelected] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}api/FGWithdrawal`, {
        headers: { Accept: "*/*" },
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message || "Failed to load records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this withdrawal form?")) return;
    setDeleting(id);
    try {
      const res = await fetch(`${API_BASE_URL}api/FGWithdrawal/${id}`, {
        method: "DELETE",
        headers: { Accept: "*/*" },
      });
      if (!res.ok) throw new Error(`Delete failed ${res.status}`);
      setData((d) => d.filter((r) => r.id !== id));
      if (viewItem?.id === id) setViewItem(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const filtered = data.filter((row) => {
    const matchSearch =
      row.controlNo?.toLowerCase().includes(search.toLowerCase()) ||
      row.documentNo?.toLowerCase().includes(search.toLowerCase()) ||
      row.requestedBy?.toLowerCase().includes(search.toLowerCase()) ||
      row.withdrawnBy?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "All" || getStatus(row) === statusFilter;
    return matchSearch && matchStatus;
  });

  const toggleSelect = (id) =>
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () =>
    setSelected(selected.length === filtered.length ? [] : filtered.map((r) => r.id));

  const stats = {
    total:    data.length,
    approved: data.filter((r) => getStatus(r) === "Approved").length,
    pending:  data.filter((r) => getStatus(r) === "Pending").length,
    rejected: data.filter((r) => getStatus(r) === "Rejected").length,
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-PH") : "—";

  const getPurpose = (row) =>
    [row.isConversion && "Conversion", row.isMarketing && "Marketing",
     row.isRepacking && "Repacking", row.isRepair && "Repair"]
      .filter(Boolean).join(", ") || "—";

  const getTotalQty = (row) =>
    row.items?.reduce((s, i) => s + (Number(i.quantity) || 0), 0) ?? 0;

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100 font-sans">

      {/* Header */}
      <div className="bg-blue-700 px-8 py-5 flex items-center justify-between shadow-md">
        <div>
          <h1 className="text-white text-2xl font-bold tracking-tight">FG Withdrawal Records</h1>
          <p className="text-blue-200 text-sm mt-0.5">Finished Goods Withdrawal — Mega Soft Hygienic Products, Inc.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={fetchData}
            className="flex items-center gap-2 bg-white/20 text-white border border-white/30 px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-white/30 transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <button className="flex items-center gap-2 bg-white text-blue-700 font-bold px-5 py-2.5 rounded-lg text-sm hover:bg-blue-50 transition shadow">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Withdrawal
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "Total Forms", value: stats.total,    color: "text-blue-700",  bg: "border-blue-100"  },
            { label: "Approved",    value: stats.approved, color: "text-green-700", bg: "border-green-100" },
            { label: "Pending",     value: stats.pending,  color: "text-yellow-600",bg: "border-yellow-100"},
            { label: "Rejected",    value: stats.rejected, color: "text-red-600",   bg: "border-red-100"   },
          ].map((s) => (
            <div key={s.label} className={`bg-white border ${s.bg} rounded-xl p-5 shadow-sm`}>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{s.label}</p>
              <p className={`text-3xl font-bold mt-1 ${s.color}`}>
                {loading ? <span className="inline-block w-8 h-8 bg-gray-100 animate-pulse rounded" /> : s.value}
              </p>
            </div>
          ))}
        </div>

        {/* Search + Filter */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search control no., document, requestor..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-4 py-2 rounded-lg text-xs font-bold border transition ${
                  statusFilter === s
                    ? "bg-blue-700 text-white border-blue-700"
                    : "bg-white text-gray-500 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

          {selected.length > 0 && (
            <div className="bg-blue-50 border-b border-blue-100 px-6 py-3 flex items-center gap-3">
              <span className="text-sm font-semibold text-blue-700">{selected.length} selected</span>
              <button className="text-xs text-red-500 hover:text-red-700 font-semibold">Delete Selected</button>
              <button onClick={() => setSelected([])} className="text-xs text-gray-500 hover:text-gray-700 font-semibold ml-auto">Clear</button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-6 py-4 bg-red-50 border-b border-red-100 text-red-600 text-sm flex items-center gap-3">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
              <button onClick={fetchData} className="ml-auto underline text-red-500 hover:text-red-700 font-semibold">Retry</button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-5 py-3.5 w-10">
                    <input type="checkbox"
                      checked={selected.length === filtered.length && filtered.length > 0}
                      onChange={toggleAll}
                      className="w-4 h-4 accent-blue-700 rounded" />
                  </th>
                  {["Control No.","Doc No.","Date","Requested By","Withdrawn By","Purpose","Qty","Status","Actions"].map((h) => (
                    <th key={h} className="text-left px-4 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  [...Array(4)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(10)].map((_, j) => (
                        <td key={j} className="px-4 py-4">
                          <div className="h-4 bg-gray-100 animate-pulse rounded w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-16 text-gray-400 text-sm">
                      <svg className="w-10 h-10 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      No withdrawal forms found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <tr key={row.id}
                      className={`hover:bg-blue-50/30 transition-colors ${selected.includes(row.id) ? "bg-blue-50/40" : ""}`}>
                      <td className="px-5 py-4">
                        <input type="checkbox" checked={selected.includes(row.id)} onChange={() => toggleSelect(row.id)}
                          className="w-4 h-4 accent-blue-700 rounded" />
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-mono text-xs text-blue-700 font-bold bg-blue-50 px-2 py-1 rounded">
                          {row.controlNo}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-600">{row.documentNo || "—"}</td>
                      <td className="px-4 py-4 text-gray-600 whitespace-nowrap">{formatDate(row.dateWithdrawn)}</td>
                      <td className="px-4 py-4 text-gray-800 font-medium">{row.requestedBy || "—"}</td>
                      <td className="px-4 py-4 text-gray-600">{row.withdrawnBy || "—"}</td>
                      <td className="px-4 py-4">
                        <span className="text-xs bg-gray-100 text-gray-600 border border-gray-200 px-2.5 py-1 rounded-full font-semibold">
                          {getPurpose(row)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center font-bold text-gray-700">{getTotalQty(row)}</td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1.5 rounded-full ${STATUS_STYLES[getStatus(row)] || STATUS_STYLES.Pending}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[getStatus(row)] || STATUS_DOT.Pending}`} />
                          {getStatus(row)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setViewItem(row)} title="View"
                            className="text-gray-400 hover:text-blue-600 transition p-1.5 rounded-lg hover:bg-blue-50">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button title="Edit"
                            className="text-gray-400 hover:text-yellow-600 transition p-1.5 rounded-lg hover:bg-yellow-50">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button onClick={() => handleDelete(row.id)} disabled={deleting === row.id} title="Delete"
                            className="text-gray-400 hover:text-red-500 transition p-1.5 rounded-lg hover:bg-red-50 disabled:opacity-40">
                            {deleting === row.id ? (
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              Showing {filtered.length} of {data.length} records
            </span>
            <div className="flex gap-1">
              <button className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">← Prev</button>
              <button className="px-3 py-1.5 text-xs bg-blue-700 text-white rounded-lg">1</button>
              <button className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">Next →</button>
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {viewItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-blue-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold text-lg">Withdrawal Details</h3>
                <p className="text-blue-200 text-xs font-mono">{viewItem.controlNo}</p>
              </div>
              <button onClick={() => setViewItem(null)} className="text-white/70 hover:text-white transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
              {[
                ["ID",             `#${viewItem.id}`],
                ["Control No.",    viewItem.controlNo],
                ["Document No.",   viewItem.documentNo || "—"],
                ["Date Withdrawn", formatDate(viewItem.dateWithdrawn)],
                ["Requested By",   viewItem.requestedBy || "—"],
                ["Acknowledged By",viewItem.acknowledgedBy || "—"],
                ["Withdrawn By",   viewItem.withdrawnBy || "—"],
                ["Received By",    viewItem.receivedBy || "—"],
                ["Purpose",        getPurpose(viewItem)],
                ["Total Items",    viewItem.items?.length ?? 0],
                ["Total Qty",      getTotalQty(viewItem)],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-gray-100 pb-2">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">{k}</span>
                  <span className="text-sm font-semibold text-gray-800">{v}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Status</span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${STATUS_STYLES[getStatus(viewItem)]}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[getStatus(viewItem)]}`} />
                  {getStatus(viewItem)}
                </span>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button onClick={() => setViewItem(null)}
                className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-50 transition">
                Close
              </button>
              <button className="flex-1 bg-blue-700 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-800 transition">
                Edit Form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}